import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { SalesDoc, CreateSalesDocPayload, UpdateSalesDocPayload } from '../types';

/** Canonical key — list page, detail drawer, and any future "Recent Docs" widget share it. */
export const SALES_DOCS_KEY = ['salesDocs', 'list'] as const;

/**
 * Single canonical Sales Documents hook. Returns the full list (backend scopes
 * by role/department). A future "Recent Sales Documents" dashboard widget can
 * consume this exact hook/cache (e.g. `data.slice(0, n)`) with no changes.
 */
export function useSalesDocs() {
  return useQuery({
    queryKey: SALES_DOCS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ docs: SalesDoc[] }>('/api/sales-docs');
      return data.docs ?? [];
    },
    staleTime: 60_000,
  });
}

export function useSalesDocMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['salesDocs'] });

  const createDoc = useMutation({
    mutationFn: async (payload: CreateSalesDocPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/sales-docs', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateDoc = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateSalesDocPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/sales-docs/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteDoc = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/sales-docs/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createDoc, updateDoc, deleteDoc };
}

/** Optional file upload via the EXISTING generic endpoint; returns the stored URL. */
export function useFileUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ url: string }>('/api/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    },
  });
}
